import {
  OperatorsSelectionType,
  VehiclesSelectionType,
} from '../../../../components/display/tables/formTables/OperatorsSelectionTable/types';

/** Un `VehicleOccupant` resuelto: su vehículo y su conductor. */
export type CrewOccupantRef = {
  vehicleIDs?: string[];
  userDriverIDs?: string[];
} | null;

/**
 * La tripulación ACTUAL de un recorrido en la forma del bloque «Operarios y
 * vehículos» (la semilla de su borrador). Extraído del loader de EditRoute
 * (F4 2026-07-17) para que «Mover tramos» (plan 2026-10-15 § 3) prellene el
 * recorrido nuevo con la MISMA lógica.
 *
 * - Operarios: el catálogo filtrado por los `userIDs` del recorrido.
 * - Vehículos: cada `VehicleOccupant` resuelto a su pareja vehículo→conductor.
 *   `vehicleTable` es la fuente que lee `deriveCrew` (Record vehicleId →
 *   { vehicleID, driverID }); la fila sale completa del catálogo para que el
 *   vehículo se vea seleccionado igual que al crear. El fallback mínimo es la
 *   red de seguridad si el vehículo no resuelve (borrado u otro uso):
 *   `deriveCrew` solo necesita vehicleID/driverID y la pareja se conserva.
 */
export async function buildRouteCrewSeed(
  route: { userIDs?: string[] | null; vehicleOccupantIDs?: string[] | null },
  catalog: {
    operators: OperatorsSelectionType[];
    vehicles: VehiclesSelectionType[];
  },
  readOccupant: (id: string) => Promise<CrewOccupantRef>
): Promise<{
  operatorsTable: OperatorsSelectionType[];
  vehicleTable: Record<string, VehiclesSelectionType>;
  assignments: Array<{ vehicleId: string; driverId: string }>;
  /**
   * Operarios del recorrido que no están en el catálogo (dados de baja): no
   * se pueden copiar y quien prellena lo tiene que decir (QA 15-oct: si no,
   * se marcaba a un homónimo activo y las horas iban a otra persona).
   */
  missingOperatorIds: string[];
}> {
  const routeUserIds = route.userIDs ?? [];
  const operatorsTable = (catalog.operators || []).filter(
    (op) => op && routeUserIds.includes(op.id)
  );
  const vehicleCatalogById = new Map<string, VehiclesSelectionType>(
    (catalog.vehicles || []).map((v) => [v.id, v])
  );
  const vehicleTable: Record<string, VehiclesSelectionType> = {};
  const assignments: Array<{ vehicleId: string; driverId: string }> = [];
  const occupants = await Promise.all(
    (route.vehicleOccupantIDs ?? []).map((id) => readOccupant(id))
  );
  occupants.forEach((occ) => {
    const vehicleId = occ?.vehicleIDs?.[0];
    const driverId = occ?.userDriverIDs?.[0];
    if (vehicleId && driverId) {
      const catalogVehicle = vehicleCatalogById.get(vehicleId);
      vehicleTable[vehicleId] = catalogVehicle
        ? { ...catalogVehicle, vehicleID: vehicleId, driverID: driverId }
        : {
            id: vehicleId,
            vehicleID: vehicleId,
            driverID: driverId,
            plate: '',
            concession: '',
          };
      assignments.push({ vehicleId, driverId });
    }
  });
  const known = new Set(operatorsTable.map((op) => op.id));
  const missingOperatorIds = routeUserIds.filter((id) => !known.has(id));
  return { operatorsTable, vehicleTable, assignments, missingOperatorIds };
}
